import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart2,
  Activity,
  DollarSign,
  Percent,
  Target,
  History,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  Calendar,
  Download,
  RefreshCw,
  Eye,
  EyeOff,
  Layers,
  Shield,
  Zap,
  Clock,
  ExternalLink,
} from 'lucide-react';

interface PortfolioAsset {
  symbol: string;
  name: string;
  balance: number;
  value: number;
  price: number;
  change24h: number;
  avgBuyPrice: number;
  pnl: number;
  pnlPercent: number;
  allocation: number;
  color: string;
}

interface PerformanceDataPoint {
  timestamp: number;
  value: number;
  pnl: number;
}

interface Trade {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  amount: number;
  price: number;
  total: number;
  fee: number;
  timestamp: number;
  pnl?: number;
}

// Empty portfolio for new users - data will come from API
const EMPTY_PORTFOLIO: PortfolioAsset[] = [];

// Generate empty performance data for new users
const generateEmptyPerformanceData = (): PerformanceDataPoint[] => {
  return [];
};

export default function Portfolio() {
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d' | 'all'>('7d');
  const [showBalances, setShowBalances] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'history'>('overview');
  const chartRef = useRef<HTMLCanvasElement>(null);
  
  // Fetch real portfolio data from API
  const { data: portfolioData } = useQuery({
    queryKey: ['portfolio'],
    queryFn: async () => {
      try {
        const response = await api.get('/trading/portfolio');
        return response.data?.assets || [];
      } catch {
        return [];
      }
    },
    staleTime: 30000,
  });

  // Fetch real trade history from API  
  const { data: tradesData } = useQuery({
    queryKey: ['trades'],
    queryFn: async () => {
      try {
        const response = await api.get('/trading/history');
        return response.data?.trades || [];
      } catch {
        return [];
      }
    },
    staleTime: 30000,
  });

  const portfolio = portfolioData || EMPTY_PORTFOLIO;
  const trades = tradesData || [];
  const performanceData = generateEmptyPerformanceData();

  // Calculate totals
  const totals = useMemo(() => {
    const totalValue = portfolio.reduce((sum: number, a: PortfolioAsset) => sum + a.value, 0);
    const totalPnL = portfolio.reduce((sum: number, a: PortfolioAsset) => sum + a.pnl, 0);
    const totalPnLPercent = totalValue > 0 ? (totalPnL / (totalValue - totalPnL)) * 100 : 0;
    const totalChange24h = portfolio.reduce((sum: number, a: PortfolioAsset) => sum + (a.value * a.change24h / 100), 0);
    const change24hPercent = totalValue > 0 ? (totalChange24h / (totalValue - totalChange24h)) * 100 : 0;
    
    return { totalValue, totalPnL, totalPnLPercent, totalChange24h, change24hPercent };
  }, [portfolio]);

  // Draw performance chart
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas || performanceData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const padding = { top: 20, right: 20, bottom: 30, left: 60 };

    // Clear
    ctx.fillStyle = '#080b0f';
    ctx.fillRect(0, 0, width, height);

    // Find range
    const values = performanceData.map(d => d.value);
    const minVal = Math.min(...values) * 0.99;
    const maxVal = Math.max(...values) * 1.01;
    const valRange = maxVal - minVal;

    // Coordinate helpers
    const xToIndex = (x: number) => Math.floor((x - padding.left) / (width - padding.left - padding.right) * performanceData.length);
    const indexToX = (i: number) => padding.left + (i / (performanceData.length - 1)) * (width - padding.left - padding.right);
    const valueToY = (v: number) => padding.top + (1 - (v - minVal) / valRange) * (height - padding.top - padding.bottom);

    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (i / 5) * (height - padding.top - padding.bottom);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    // Draw area
    const isProfit = performanceData[performanceData.length - 1].value >= performanceData[0].value;
    const mainColor = isProfit ? '#00D4AA' : '#FF6B6B';

    ctx.beginPath();
    ctx.moveTo(indexToX(0), valueToY(performanceData[0].value));
    for (let i = 1; i < performanceData.length; i++) {
      ctx.lineTo(indexToX(i), valueToY(performanceData[i].value));
    }
    ctx.lineTo(indexToX(performanceData.length - 1), height - padding.bottom);
    ctx.lineTo(indexToX(0), height - padding.bottom);
    ctx.closePath();

    const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
    gradient.addColorStop(0, `${mainColor}30`);
    gradient.addColorStop(1, `${mainColor}05`);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw line
    ctx.beginPath();
    ctx.strokeStyle = mainColor;
    ctx.lineWidth = 2;
    ctx.moveTo(indexToX(0), valueToY(performanceData[0].value));
    for (let i = 1; i < performanceData.length; i++) {
      ctx.lineTo(indexToX(i), valueToY(performanceData[i].value));
    }
    ctx.stroke();

    // Y-axis labels
    ctx.fillStyle = '#8b949e';
    ctx.font = '11px "JetBrains Mono", monospace';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const val = maxVal - (i / 5) * valRange;
      const y = valueToY(val);
      ctx.fillText('$' + (val / 1000).toFixed(1) + 'K', padding.left - 8, y + 4);
    }

    // X-axis labels
    ctx.textAlign = 'center';
    const labelCount = 5;
    for (let i = 0; i <= labelCount; i++) {
      const idx = Math.floor((i / labelCount) * (performanceData.length - 1));
      const x = indexToX(idx);
      const date = new Date(performanceData[idx].timestamp);
      const label = timeRange === '24h' 
        ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      ctx.fillText(label, x, height - 10);
    }

  }, [performanceData, timeRange]);

  // Format helpers
  const formatValue = (value: number, hide = false) => {
    if (hide && !showBalances) return '•••••';
    if (value >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
    if (value >= 1e3) return '$' + (value / 1e3).toFixed(2) + 'K';
    return '$' + value.toFixed(2);
  };

  const formatPercent = (pct: number) => {
    const sign = pct >= 0 ? '+' : '';
    return sign + pct.toFixed(2) + '%';
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#080b0f] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Wallet className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Portfolio</h1>
              <p className="text-sm text-[#8b949e]">Track your holdings and performance</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBalances(!showBalances)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.04] text-[#8b949e] hover:text-white transition-colors"
            >
              {showBalances ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {showBalances ? 'Hide' : 'Show'} Balances
            </button>
            <button className="p-2 rounded-lg bg-white/[0.04] text-[#8b949e] hover:text-white transition-colors">
              <Download className="h-4 w-4" />
            </button>
            <button className="p-2 rounded-lg bg-white/[0.04] text-[#8b949e] hover:text-white transition-colors">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#8b949e]">Total Value</span>
              <DollarSign className="h-4 w-4 text-primary" />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {formatValue(totals.totalValue, true)}
            </div>
            <div className={cn(
              "flex items-center gap-1 text-sm",
              totals.change24hPercent >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
            )}>
              {totals.change24hPercent >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              {formatPercent(totals.change24hPercent)} (24h)
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#8b949e]">Total P&L</span>
              <TrendingUp className="h-4 w-4 text-[#00D4AA]" />
            </div>
            <div className={cn(
              "text-2xl font-bold mb-1",
              totals.totalPnL >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
            )}>
              {formatValue(Math.abs(totals.totalPnL), true)}
            </div>
            <div className={cn(
              "flex items-center gap-1 text-sm",
              totals.totalPnLPercent >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
            )}>
              {formatPercent(totals.totalPnLPercent)} all time
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#8b949e]">Assets</span>
              <Layers className="h-4 w-4 text-[#8B5CF6]" />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {portfolio.length}
            </div>
            <div className="text-sm text-[#8b949e]">
              {portfolio.filter((a: PortfolioAsset) => a.pnl > 0).length} profitable
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#8b949e]">Best Performer</span>
              <Zap className="h-4 w-4 text-[#F59E0B]" />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {portfolio.length > 0 ? [...portfolio].sort((a: PortfolioAsset, b: PortfolioAsset) => b.pnlPercent - a.pnlPercent)[0]?.symbol : '-'}
            </div>
            <div className="text-sm text-[#00D4AA]">
              {portfolio.length > 0 ? formatPercent([...portfolio].sort((a: PortfolioAsset, b: PortfolioAsset) => b.pnlPercent - a.pnlPercent)[0]?.pnlPercent || 0) : '-'}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-3 gap-6">
          {/* Performance Chart */}
          <div className="col-span-2 card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Portfolio Performance</h2>
              <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
                {(['24h', '7d', '30d', '90d', 'all'] as const).map(range => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                      timeRange === range
                        ? "bg-primary text-[#080b0f]"
                        : "text-[#8b949e] hover:text-white"
                    )}
                  >
                    {range.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <canvas ref={chartRef} className="w-full h-64" />
          </div>

          {/* Allocation Pie Chart */}
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-white mb-4">Allocation</h2>
            <div className="relative w-48 h-48 mx-auto mb-4">
              <svg viewBox="0 0 100 100" className="transform -rotate-90">
                {(() => {
                  let cumulative = 0;
                  return portfolio.map((asset: PortfolioAsset, i: number) => {
                    const start = cumulative;
                    cumulative += asset.allocation;
                    const largeArc = asset.allocation > 50 ? 1 : 0;
                    const startX = 50 + 40 * Math.cos(2 * Math.PI * start / 100);
                    const startY = 50 + 40 * Math.sin(2 * Math.PI * start / 100);
                    const endX = 50 + 40 * Math.cos(2 * Math.PI * cumulative / 100);
                    const endY = 50 + 40 * Math.sin(2 * Math.PI * cumulative / 100);
                    
                    return (
                      <path
                        key={asset.symbol}
                        d={`M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArc} 1 ${endX} ${endY} Z`}
                        fill={asset.color}
                        className="hover:opacity-80 transition-opacity cursor-pointer"
                      />
                    );
                  });
                })()}
                <circle cx="50" cy="50" r="25" fill="#080b0f" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-xs text-[#8b949e]">Total</div>
                  <div className="text-lg font-bold text-white">{formatValue(totals.totalValue, true)}</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              {portfolio.slice(0, 5).map((asset: PortfolioAsset) => (
                <div key={asset.symbol} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: asset.color }} />
                    <span className="text-white font-medium">{asset.symbol}</span>
                  </div>
                  <span className="text-[#8b949e]">{asset.allocation.toFixed(1)}%</span>
                </div>
              ))}
              {portfolio.length > 5 && (
                <div className="text-xs text-[#8b949e] text-center pt-2">
                  +{portfolio.length - 5} more assets
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mt-6 border-b border-white/[0.06]">
          {[
            { id: 'overview', label: 'Assets', icon: Layers },
            { id: 'history', label: 'Trade History', icon: History },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-px",
                  activeTab === tab.id
                    ? "text-primary border-primary"
                    : "text-[#8b949e] border-transparent hover:text-white"
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Assets Table */}
        {activeTab === 'overview' && (
          <div className="mt-4">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-[#8b949e] border-b border-white/[0.06]">
                  <th className="px-4 py-3 text-left font-semibold">Asset</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance</th>
                  <th className="px-4 py-3 text-right font-semibold">Price</th>
                  <th className="px-4 py-3 text-right font-semibold">24h Change</th>
                  <th className="px-4 py-3 text-right font-semibold">Value</th>
                  <th className="px-4 py-3 text-right font-semibold">Avg Buy</th>
                  <th className="px-4 py-3 text-right font-semibold">P&L</th>
                  <th className="px-4 py-3 text-right font-semibold">Allocation</th>
                </tr>
              </thead>
              <tbody>
                {portfolio.map((asset: PortfolioAsset) => (
                  <tr key={asset.symbol} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${asset.color}20` }}
                        >
                          <span className="text-sm font-bold" style={{ color: asset.color }}>
                            {asset.symbol.slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-white">{asset.symbol}</div>
                          <div className="text-xs text-[#8b949e]">{asset.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono text-white">
                        {showBalances ? asset.balance.toFixed(4) : '•••••'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono text-white">${asset.price.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className={cn(
                        "font-mono",
                        asset.change24h >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                      )}>
                        {formatPercent(asset.change24h)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono text-white font-medium">
                        {formatValue(asset.value, true)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono text-[#8b949e]">${asset.avgBuyPrice.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className={cn(
                        "font-mono font-medium",
                        asset.pnl >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                      )}>
                        {showBalances ? (asset.pnl >= 0 ? '+' : '') + '$' + Math.abs(asset.pnl).toLocaleString() : '•••••'}
                      </div>
                      <div className={cn(
                        "text-xs",
                        asset.pnlPercent >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                      )}>
                        {formatPercent(asset.pnlPercent)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-white/[0.1] overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{ width: `${asset.allocation}%`, backgroundColor: asset.color }}
                          />
                        </div>
                        <span className="font-mono text-sm text-[#8b949e] w-12 text-right">
                          {asset.allocation.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Trade History */}
        {activeTab === 'history' && (
          <div className="mt-4">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-[#8b949e] border-b border-white/[0.06]">
                  <th className="px-4 py-3 text-left font-semibold">Time</th>
                  <th className="px-4 py-3 text-left font-semibold">Asset</th>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  <th className="px-4 py-3 text-right font-semibold">Price</th>
                  <th className="px-4 py-3 text-right font-semibold">Total</th>
                  <th className="px-4 py-3 text-right font-semibold">Fee</th>
                  <th className="px-4 py-3 text-right font-semibold">P&L</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade: Trade) => (
                  <tr key={trade.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm text-white">{new Date(trade.timestamp).toLocaleDateString()}</div>
                      <div className="text-xs text-[#8b949e]">{new Date(trade.timestamp).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-white">{trade.symbol}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-1 rounded text-xs font-bold",
                        trade.side === 'buy' 
                          ? "bg-[#00D4AA]/20 text-[#00D4AA]" 
                          : "bg-[#FF6B6B]/20 text-[#FF6B6B]"
                      )}>
                        {trade.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-white">{trade.amount.toFixed(4)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-white">${trade.price.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-white font-medium">${trade.total.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-[#8b949e]">${trade.fee.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {trade.pnl !== undefined ? (
                        <span className={cn(
                          "font-mono font-medium",
                          trade.pnl >= 0 ? "text-[#00D4AA]" : "text-[#FF6B6B]"
                        )}>
                          {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-[#8b949e]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
